import React, { useEffect, useState } from "react";
import { filter } from "lodash";
import { format, isValid, parseISO } from "date-fns";
import { saveAs } from "file-saver";
import { ZipWriter, BlobWriter, TextReader } from "@zip.js/zip.js";
import { CalendarIcon, ChevronsUpDown, Check, Download } from "lucide-react";
import { ClipLoader } from "react-spinners";
import { useNavigate, useLocation } from "react-router-dom";

// UI Components
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "../../components/ui/card";
import {
	Table,
	TableBody,
	TableCaption,
	TableCell,
	TableFooter,
	TableHead,
	TableHeader,
	TableRow,
} from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Calendar } from "../../components/ui/calendar";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
} from "../../components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "../../components/ui/popover";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "../../components/ui/sheet";
import { useToast } from "../../components/ui/use-toast";
import SgtinModal from "../../components/SgtinModal";
import Paginator from "../../components/Paginator";

// Hooks
import useAuth from "../../hooks/useAuth";
import {
	useGetSGTINData,
	useDownloadSgtinLabels,
} from "../../migration-api/api-hooks/serialization";
import {
	useGetAllClients,
	useGetClientProducts,
} from "../../migration-api/api-hooks/clients";
import { useSendEmail } from "../../migration-api/api-hooks/email";

// Utils
import { cn, convertToCSV, generateSecurePassword } from "../../lib/utils";

function Sgtin() {
	const { auth } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const { toast } = useToast();

	// Filter states
	const [startdate, setStartdate] = useState();
	const [enddate, setEnddate] = useState();
	const [openclient, setOpenClient] = useState(false);
	const [valueClient, setClientValue] = useState("");
	const [openProd, setOpenProd] = useState(false);
	const [prodValue, setProdValue] = useState("");
	const [openLabel, setOpenlabel] = useState(false);
	const [labelValue, setLabelValue] = useState("");

	// Data states
	const [selectedClientId, setSelectedClientId] = useState("");
	const [selectedProductId, setSelectedProductId] = useState("");
	const [traceId, setTraceId] = useState("");
	const [selectedLabelClientId, setSelectedLabelClientId] = useState(""); // Store clientId from selected label
	const [elemTarget, setElemTarget] = useState(null);
	const [staticDB, setStaticDB] = useState([]);
	const [staticBat, setStaticBat] = useState([]);
	const [eventList, setEventList] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageCount, setPageCount] = useState(0);

	// Geolocation states
	const [longitude, setLongitude] = useState();
	const [latitude, setLatitude] = useState();

	// Params for searching/filtering
	const [apiParams, setApiParams] = useState({
		clientIds: "all",
		prodIds: "all",
		startDate: undefined,
		endDate: undefined,
		batchLotNo: undefined,
	});

	// Custom hooks for data fetching
	const {
		data: sgtinTable,
		isPending: isLoadingTable,
		isError: isTableError,
	} = useGetSGTINData({
		currentPage,
		clientIds: apiParams.clientIds,
		prodIds: apiParams.prodIds,
		startDate: apiParams.startDate,
		endDate: apiParams.endDate,
		batchLotNo: apiParams.batchLotNo,
	});

	// Fetch all clients
	const { data: clientsData, isLoading: isLoadingClients } =
		useGetAllClients();
	const allClients = clientsData?.data || [];

	// Fetch products for selected client
	const { data: clientProductsData, isLoading: isLoadingProducts } =
		useGetClientProducts(selectedClientId, { enabled: !!selectedClientId });
	const prods = clientProductsData?.data || [];

	const {
		downloadLabels,
		isLoading: isDownloading,
		data: downloadData,
		isSuccess: isDownloadSuccess,
	} = useDownloadSgtinLabels({
		onSuccess: (data) => {
			// Process the download immediately when successful
			downloadCSVAsEncryptedZip(data);
			if (elemTarget) {
				elemTarget.target.checked = false;
			}
		},
		onError: handleError,
	});

	const { sendEmail } = useSendEmail({
		onSuccess: () => {
			toast({
				variant: "success",
				title: "Password Sent",
				description:
					"The password for the downloaded SGTIN file has been sent to your email",
			});
		},
		onError: handleError,
	});

	// Geolocation effect
	useEffect(() => {
		navigator.permissions.query({ name: "geolocation" }).then((resp) => {
			if (resp.state === "granted" || resp.state === "prompt") {
				if (navigator.geolocation) {
					navigator.geolocation.getCurrentPosition(
						(position) => {
							setLatitude(`${position.coords.latitude}`);
							setLongitude(`${position.coords.longitude}`);
						},
						(err) => {
							console.error(err);
							navigate("/", {
								state: { from: location, replace: true },
							});
						},
					);
				}
			} else if (resp.state === "denied") {
				navigate("/", { state: { from: location, replace: true } });
			}
		});
	}, [navigate, location]);

	// Process SGTIN table data
	useEffect(() => {
		if (!sgtinTable) return;

		const rows = sgtinTable.rows ?? [];
		setEventList(rows);
		setStaticDB(rows);

		const batches = Array.from(new Set(rows.map((item) => item)));
		setStaticBat(batches);
		setPageCount(sgtinTable.pageCount ?? 0);
	}, [sgtinTable]);

	// Handlers
	function handleError(error) {
		if (error.code === "ERR_NETWORK") {
			console.error(error);
			navigate("/", { state: { from: location }, replace: true });
		} else if (
			error.response?.status === 403 ||
			error.response?.status === 401
		) {
			navigate("/", { state: { from: location }, replace: true });
		} else {
			toast({
				variant: "destructive",
				title: "Error",
				description: `${error?.response?.data?.message || "An error occurred"}`,
			});
		}
	}

	const handleClientChange = (client) => {
		setClientValue(client.clientName);
		setSelectedClientId(client.id);
		setOpenClient(false);
		// Reset product selection when client changes
		setProdValue("");
		setSelectedProductId("");
	};

	const handleDownload = () => {
		if (!traceId) {
			toast({
				variant: "destructive",
				title: "Error",
				description: "Please select a label to download",
			});
			return;
		}

		if (!selectedLabelClientId) {
			toast({
				variant: "destructive",
				title: "Error",
				description: "Invalid label selection. Client ID not found.",
			});
			return;
		}

		downloadLabels({ clientId: selectedLabelClientId, traceId });
	};

	const handleParams = () => {
		const dateEnd =
			enddate !== undefined
				? format(enddate, "yyyy-MM-dd HH:mm:ss")
				: format(new Date(), "yyyy-MM-dd HH:mm:ss");
		const dateStart =
			startdate !== undefined
				? format(startdate, "yyyy-MM-dd HH:mm:ss")
				: "2000-01-01";

		// Set API parameters to trigger new fetch
		setApiParams({
			clientIds: selectedClientId || "all",
			prodIds: selectedProductId || "all",
			startDate: dateStart,
			endDate: dateEnd,
			batchLotNo:
				labelValue && labelValue !== "all" ? labelValue : undefined,
		});

		// Reset to page 1 when searching
		setCurrentPage(1);
	};

	const handleDate = (date) => {
		if (date == null) return "N/A";
		const validDate = parseISO(date, "yyyy-MM-dd", new Date());
		if (isValid(validDate)) {
			return format(new Date(date), "yyyy-MM-dd HH:mm:ss");
		}
		return "N/A";
	};

	const onPageChange = (page) => {
		setCurrentPage(page);
	};

	async function downloadCSVAsEncryptedZip(data) {
		if (!data || !traceId) {
			console.error("Missing data or traceId for download");
			return;
		}

		const password = generateSecurePassword();
		const sgtinHeader = [
			{ field: "GTIN", key: "gtin" },
			{ field: "BatchNumber", key: "batchLotNo" },
			{ field: "ExpiryDate", key: "expDate" },
			{ field: "SerialNumber", key: "sn" },
			{ field: "MFG", key: "mfgDate" },
		];

		try {
			const zipWriter = new ZipWriter(new BlobWriter("application/zip"), {
				password,
				encryptionStrength: 1,
				zipCrypto: true,
			});

			const csvString = convertToCSV(data, sgtinHeader);

			await zipWriter.add(
				`encoded-sgtin-${traceId}-sproxil.csv`,
				new TextReader(csvString),
				{ password },
			);

			const zipBlob = await zipWriter.close();
			saveAs(zipBlob, "sgtin-data.zip");

			sendEmail({
				emailTo: auth?.user?.email,
				subject: "SGTIN File Password",
				html: `<html><body><p>Your password for the SGTIN file you just downloaded is:</p><h2>${password}</h2></body></html>`,
			});

			// Clean up after successful download
			setTraceId("");
			setSelectedLabelClientId("");
			setElemTarget(null);
		} catch (error) {
			console.error("Error creating encrypted zip:", error);
			toast({
				variant: "destructive",
				title: "Download Error",
				description: "Failed to create encrypted download file",
			});
		}
	}

	return (
		<div className="overflow-x-hidden">
			<Card className="overflow-x-hidden">
				<CardHeader>
					<CardTitle className="my-[10px] flex justify-between items-center p-[20px]">
						<span className="flex gap-[20px] items-center">
							<span>SGTIN Generation Tab</span>
							<span>
								<Sheet>
									<SheetTrigger asChild>
										<Button variant="outline">
											Generate Label
										</Button>
									</SheetTrigger>
									<SheetContent className="min-w-[550px]">
										<SheetHeader>
											<SheetTitle>
												Generate Label
											</SheetTitle>
											<SheetDescription>
												Enter the required GTIN details
												here. Click submit when you're
												done.
											</SheetDescription>
										</SheetHeader>
										<SgtinModal
											longitude={longitude}
											latitude={latitude}
										/>
									</SheetContent>
								</Sheet>
							</span>
						</span>
						<span>
							<Button
								onClick={handleDownload}
								disabled={isDownloading || !traceId}
							>
								{isDownloading ? (
									<ClipLoader color="primary" size={20} />
								) : (
									<Download />
								)}
							</Button>
						</span>
					</CardTitle>

					<CardDescription className="flex gap-[10px] flex-wrap">
						{/* Client Selector */}
						<Popover open={openclient} onOpenChange={setOpenClient}>
							<PopoverTrigger asChild>
								<Button
									variant="outline"
									role="combobox"
									aria-expanded={openclient}
									className="w-[200px] justify-between"
									disabled={isLoadingClients}
								>
									{isLoadingClients
										? "Loading clients..."
										: valueClient
											? valueClient
											: "Pick a client..."}
									<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
								</Button>
							</PopoverTrigger>
							<PopoverContent className="w-[200px] p-0">
								<Command>
									<CommandInput placeholder="Search client..." />
									<CommandEmpty>
										No client found.
									</CommandEmpty>
									<CommandGroup>
										{allClients.map((client) => (
											<CommandItem
												key={client.id}
												value={client.clientName}
												onSelect={() =>
													handleClientChange(client)
												}
											>
												<Check
													className={cn(
														"mr-2 h-4 w-4",
														valueClient ===
															client.clientName
															? "opacity-100"
															: "opacity-0",
													)}
												/>
												{client.clientName}
											</CommandItem>
										))}
									</CommandGroup>
								</Command>
							</PopoverContent>
						</Popover>

						{/* Product Selector */}
						<Popover open={openProd} onOpenChange={setOpenProd}>
							<PopoverTrigger asChild>
								<Button
									variant="outline"
									role="combobox"
									aria-expanded={openProd}
									className="w-[200px] justify-between"
									disabled={
										!selectedClientId || isLoadingProducts
									}
								>
									{!selectedClientId
										? "Select client first..."
										: isLoadingProducts
											? "Loading products..."
											: prodValue
												? prodValue
												: "Pick a product..."}
									<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
								</Button>
							</PopoverTrigger>
							<PopoverContent className="w-[200px] p-0">
								<Command>
									<CommandInput placeholder="Search product..." />
									<CommandEmpty>
										No product found.
									</CommandEmpty>
									<CommandGroup>
										{prods.map((prod, index) => (
											<CommandItem
												key={index}
												value={prod.prodName}
												onSelect={(currentValue) => {
													setProdValue(currentValue);
													setSelectedProductId(
														prod.id,
													);
													setOpenProd(false);
												}}
											>
												<Check
													className={cn(
														"mr-2 h-4 w-4",
														prodValue ===
															prod.prodName
															? "opacity-100"
															: "opacity-0",
													)}
												/>
												{prod.prodName}
											</CommandItem>
										))}
									</CommandGroup>
								</Command>
							</PopoverContent>
						</Popover>

						{/* Batch/Lot Selector */}
						<Popover open={openLabel} onOpenChange={setOpenlabel}>
							<PopoverTrigger asChild>
								<Button
									variant="outline"
									role="combobox"
									aria-expanded={openLabel}
									className="w-[200px] justify-between"
								>
									{labelValue
										? labelValue
										: "Pick Batch/lot..."}
									<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
								</Button>
							</PopoverTrigger>
							<PopoverContent className="w-[200px] p-0">
								<Command>
									<CommandInput placeholder="Search batch/lot..." />
									<CommandEmpty>
										No batch/lot found.
									</CommandEmpty>
									<CommandGroup>
										{staticBat.map((batch) => (
											<CommandItem
												key={batch.traceId}
												value={batch.batchLotNo}
												onSelect={(currentValue) => {
													setLabelValue(
														currentValue ===
															labelValue
															? ""
															: currentValue,
													);
													setOpenlabel(false);
												}}
											>
												<Check
													className={cn(
														"mr-2 h-4 w-4",
														labelValue ===
															batch.batchLotNo
															? "opacity-100"
															: "opacity-0",
													)}
												/>
												{batch.batchLotNo}
											</CommandItem>
										))}
									</CommandGroup>
								</Command>
							</PopoverContent>
						</Popover>

						{/* Start Date */}
						<Popover>
							<PopoverTrigger asChild>
								<Button
									variant="outline"
									className={cn(
										"w-[280px] justify-start text-left font-normal",
										!startdate && "text-muted-foreground",
									)}
								>
									<CalendarIcon className="mr-2 h-4 w-4" />
									{startdate ? (
										format(startdate, "PPP")
									) : (
										<span>Start date</span>
									)}
								</Button>
							</PopoverTrigger>
							<PopoverContent className="w-auto p-0">
								<Calendar
									mode="single"
									selected={startdate}
									onSelect={setStartdate}
									initialFocus
								/>
							</PopoverContent>
						</Popover>

						{/* End Date */}
						<Popover>
							<PopoverTrigger asChild>
								<Button
									variant="outline"
									className={cn(
										"w-[280px] justify-start text-left font-normal",
										!enddate && "text-muted-foreground",
									)}
								>
									<CalendarIcon className="mr-2 h-4 w-4" />
									{enddate ? (
										format(enddate, "PPP")
									) : (
										<span>End date</span>
									)}
								</Button>
							</PopoverTrigger>
							<PopoverContent className="w-auto p-0">
								<Calendar
									mode="single"
									selected={enddate}
									onSelect={setEnddate}
									initialFocus
								/>
							</PopoverContent>
						</Popover>

						<Button onClick={handleParams}>Search</Button>
					</CardDescription>
				</CardHeader>

				<CardContent className="space-y-2">
					{isLoadingTable ? (
						<div className="flex justify-center items-center p-12">
							<div className="text-center">
								<ClipLoader color="primary" size={50} />
								<p className="mt-4 text-gray-600">
									Loading SGTIN labels...
								</p>
							</div>
						</div>
					) : (
						<Table className="overflow-x-hidden">
							<TableCaption>
								A list of all generated SGTIN labels.
							</TableCaption>
							<TableHeader>
								<TableRow>
									<TableHead></TableHead>
									<TableHead>Product Name</TableHead>
									<TableHead>Client Name</TableHead>
									<TableHead>Date Generated</TableHead>
									<TableHead>Generated By</TableHead>
									<TableHead>BATCH/LOT</TableHead>
									<TableHead className="text-right whitespace-nowrap">
										Expiry Date
									</TableHead>
									<TableHead className="text-right whitespace-nowrap">
										MFG Date
									</TableHead>
									<TableHead>Volume</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{eventList.length === 0 ? (
									<TableRow>
										<TableCell
											colSpan={9}
											className="text-center"
										>
											No SGTIN labels found
										</TableCell>
									</TableRow>
								) : (
									eventList.map((event) => (
										<TableRow key={event.id}>
											<TableCell>
												<input
													style={{
														cursor: "pointer",
													}}
													type="radio"
													name="sgtinTrace"
													onChange={(e) => {
														setTraceId(
															event.traceId,
														);
														setSelectedLabelClientId(
															event.clientId,
														); // Store clientId from the selected label
														setElemTarget(e);
													}}
												/>
											</TableCell>
											<TableCell className="whitespace-nowrap">
												{event?.prodName}
											</TableCell>
											<TableCell className="whitespace-nowrap">
												{event?.clientName}
											</TableCell>
											<TableCell className="whitespace-nowrap">
												{handleDate(event?.createdAt)}
											</TableCell>
											<TableCell className="whitespace-nowrap">
												{event?.createdBy}
											</TableCell>
											<TableCell className="whitespace-nowrap">
												{event?.batchLotNo}
											</TableCell>
											<TableCell className="text-right">
												{event?.expDate}
											</TableCell>
											<TableCell className="text-right">
												{event?.mfgDate}
											</TableCell>
											<TableCell>
												{event?.count}
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
							<TableFooter>
								<TableRow>
									<TableCell colSpan={9}>Total</TableCell>
								</TableRow>
								<TableRow>
									<TableCell colSpan={9}>
										{pageCount > 1 && (
											<Paginator
												currentPage={currentPage}
												disabled={isLoadingTable}
												onPageChange={onPageChange}
												pageCount={pageCount}
											/>
										)}
									</TableCell>
								</TableRow>
							</TableFooter>
						</Table>
					)}
				</CardContent>
				<CardFooter></CardFooter>
			</Card>
		</div>
	);
}

export default Sgtin;
